import CollectionApi from 'client/api/v2/CollectionApi';

/**
 * Note: in Implementation Details on a JIRA ticket, a backend call will refer to IDs
 * differently than how they are named here. apple_mdm_id is what we refer to in this file
 * as deviceManagerId, and device_id is what we refer to in this file as systemMdmId
 */

/** @typedef {import('./types/AppleMDM')} */

class MdmsApi extends CollectionApi {
  constructor() {
    super('/api/v2/applemdms', {
      skip: 0,
      limit: 50,
    });
  }

  /**
   * @param {string} deviceManagerId - Device Manager ID
   */
  deleteMdmConfiguration(deviceManagerId) {
    return this.axios.delete(`${this.baseUrl}/${deviceManagerId}`);
  }

  /**
   * @param {string} deviceManagerId - Device Manager ID
   * @param {string} systemMdmId - System MDM Internal Id
   * (also referred to as device_id on the SI's side)

   */

  deleteMdmUserEnrollment(deviceManagerId, systemMdmId) {
    return this.axios.delete(`${this.baseUrl}/${deviceManagerId}/devices/${systemMdmId}`);
  }

  /**
   * @param {string} deviceManagerId - Device Manager ID
   * @param {string} systemMdmId - System MDM Internal Id

   */
  fetchAvailableOSUpdates(deviceManagerId, systemMdmId) {
    return this.axios.get(`${this.baseUrl}/${deviceManagerId}/devices/${systemMdmId}/availableOsUpdates`)
      .then(resp => (resp?.data ?? []));
  }

  /**
   * @param {string} deviceManagerId - Device Manager ID
   * @param {string} systemMdmId - System MDM Internal Id

   */
  fetchBypassCodes(deviceManagerId, systemMdmId) {
    return this.axios.get(`${this.baseUrl}/${deviceManagerId}/devices/${systemMdmId}/bypasscodes`)
      .then(resp => (resp?.data ?? []));
  }

  /**
   * @param {string} deviceManagerId - Device Manager ID
   */
  fetchCSRCertificate(deviceManagerId) {
    return this.axios.get(`${this.baseUrl}/${deviceManagerId}/csr`)
      .then(resp => (resp?.data ?? ''));
  }

  /**
   * Get DEP Certificate
   * @param {string} deviceManagerId - Device Manager ID
   */
  fetchDEPCertificate(deviceManagerId) {
    const headers = {
      Accept: 'application/x-pem-file',
    };

    return this.axios.get(`${this.baseUrl}/${deviceManagerId}/depkey`, { params: {}, headers })
      .then(resp => (resp?.data ?? ''));
  }

  /**
   * @param {string} deviceManagerId - Device Manager ID (apple_mdm_id on the SI side)
   * @param {string} enrollmentProfileId - Enrollment Profile ID
   *
   */

  fetchEnrollmentProfileDownloadUrl(deviceManagerId, enrollmentProfileId) {
    return this.axios.get(`${this.baseUrl}/${deviceManagerId}/enrollmentprofiles/${enrollmentProfileId}/downloadurl`)
      .then(resp => (resp?.data ?? ''));
  }

  /**
   * Gets the device manager which owns all of the
   * devices for an org. - known in MdmMixin file as fetchMdmSettings
   * @returns {Promise<import('node_modules/axios/index').AxiosResponse<DeviceManager[]>>}
   */
  fetchMdm() {
    return this.axios.get(this.baseUrl)
      .then(resp => (resp?.data ?? []));
  }

  /**
   * @param {string} deviceManagerId - Device Manager ID
   * @param {string} systemMdmId - System MDM Internal Id
   *
   * Gets the details for a specific Apple MDM device -
   * known in MdmMixin file the same as this, fetchMdmDeviceDetails
   */
  fetchMdmDeviceDetails(deviceManagerId, systemMdmId) {
    return this.axios.get(`${this.baseUrl}/${deviceManagerId}/devices/${systemMdmId}`)
      .then(resp => ((resp && resp?.data) || []));
  }

  /**
   * @param {string} deviceManagerId - Device Manager ID
   * @param {object} params - Endpoint extra param
   */
  getDepDevices(deviceManagerId, params = {}) {
    const headers = {
      Accept: 'application/json',
    };
    const filterSortParams = {
      ...params,
      filter: 'dep_registered:eq:true',
      sort: '-created_at',
    };

    return this.axios.get(`${this.baseUrl}/${deviceManagerId}/devices`, { params: filterSortParams, headers });
  }

  /**
   * @param {string} deviceManagerId - Device Manager ID
   */
  getEnrollmentProfiles(deviceManagerId) {
    return this.axios.get(`${this.baseUrl}/${deviceManagerId}/enrollmentprofiles`)
      .then(resp => (resp?.data ?? []));
  }

  /**
   * @param {string} deviceManagerId - Device Manager ID
   * @param {string} enrollmentProfileId - Enrollment Profile ID
   */
  getMobileConfig(deviceManagerId, enrollmentProfileId) {
    const headers = {
      Accept: 'application/x-apple-aspen-config',
    };
    const responseType = 'blob';

    return this.axios.get(`${this.baseUrl}/${deviceManagerId}/enrollmentprofiles/${enrollmentProfileId}`, { params: {}, responseType, headers });
  }

  /**
   * @param {string} deviceManagerId - Device Manager ID
   * @param {string} systemMdmId - System MDM Internal Id
   */
  postClearActivationLock(deviceManagerId, systemMdmId) {
    return this.axios.post(`${this.baseUrl}/${deviceManagerId}/devices/${systemMdmId}/clearActivationLock`, {})
      .then(resp => (resp?.data ?? []));
  }

  /**
   * @param {string} deviceManagerId - Device Manager ID
   * @param {string} systemMdmId - System MDM Internal Id
   * @param {string} body - install_action and product_key
   */
  postScheduleOSUpdate(deviceManagerId, systemMdmId, body) {
    return this.axios.post(`${this.baseUrl}/${deviceManagerId}/devices/${systemMdmId}/scheduleOSUpdate`, body)
      .then(resp => (resp?.data ?? []));
  }

  /**
   * @param {string} deviceManagerId - Device Manager ID
   * @param {string} systemMdmId - System MDM Internal Id
   * @param {Object} payload - Pin code
   */
  postEraseDevice(deviceManagerId, systemMdmId, payload) {
    return this.axios.post(`${this.baseUrl}/${deviceManagerId}/devices/${systemMdmId}/erase`, payload);
  }

  /**
   * @param {string} deviceManagerId - Device Manager ID
   * @param {string} systemMdmId - System MDM Internal Id
   * @param {Object} payload - Pin code
   */
  postLockDevice(deviceManagerId, systemMdmId, payload) {
    return this.axios.post(`${this.baseUrl}/${deviceManagerId}/devices/${systemMdmId}/lock`, payload);
  }

  /**
   * Creates the initial record for MDM Devices
   */
  postMdm() {
    return this.axios.post(this.baseUrl, {})
      .then(resp => (resp?.data ?? {}));
  }

  /**
   * @param {string} deviceManagerId - Device Manager ID
   * @param {string} systemMdmId - System MDM Internal Id
   */
  postRestartDevice(deviceManagerId, systemMdmId) {
    return this.axios.post(`${this.baseUrl}/${deviceManagerId}/devices/${systemMdmId}/restart`, {})
      .then(resp => (resp?.data ?? {}));
  }

  /**
   * @param {string} deviceManagerId - Device Manager ID
   */
  postRefreshDepDevices(deviceManagerId) {
    return this.axios.post(`${this.baseUrl}/${deviceManagerId}/refreshdepdevices`, {})
      .then(resp => (resp?.data ?? {}));
  }

  /**
   * @param {string} deviceManagerId - Device Manager ID
   * @param {string} systemMdmId - System MDM Internal Id
   */
  postShutdownDevice(deviceManagerId, systemMdmId) {
    return this.axios.post(`${this.baseUrl}/${deviceManagerId}/devices/${systemMdmId}/shutdown`, {})
      .then(resp => (resp?.data ?? {}));
  }

  /**
   * @param {string} deviceManagerId - Device Manager ID
   * @param {string} systemMdmId - System MDM Internal Id
   */
  refreshActivationLockInformation(deviceManagerId, systemMdmId) {
    return this.axios.post(`${this.baseUrl}/${deviceManagerId}/devices/${systemMdmId}/refreshActivationLockInformation`, {})
      .then(resp => (resp?.data ?? []));
  }

  /**
   * Update the information related to a DEP device
   * @param {string} deviceManagerId - Device Manager ID
   * @param {Object} payload - New settings
   */
  updateDep(deviceManagerId, payload) {
    return this.axios.put(`${this.baseUrl}/${deviceManagerId}`, payload);
  }
}

export default new MdmsApi();
